import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { COLORS } from '../../../../core/constants/colors';
import { AdminDocument } from '../types';
import { authService } from '../../../../features/auth/services/auth.service';

// Set up PDF.js worker - use CDN version that matches react-pdf's internal pdfjs-dist version
// react-pdf uses pdfjs-dist 5.4.296, so we use the matching worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;

interface DocumentViewerModalProps {
  document: AdminDocument | null;
  isOpen: boolean;
  onClose: () => void;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  document,
  isOpen,
  onClose,
}) => {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [, setDocumentBlob] = useState<Blob | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [detectedFileType, setDetectedFileType] = useState<string>('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    if (isOpen && document) {
      loadDocument();
    } else {
      // Cleanup
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
        setDocumentUrl(null);
      }
      setDocumentBlob(null);
      setTextContent(null);
      setError('');
      setDetectedFileType('');
      setNumPages(null);
      setPageNumber(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, document]);

  const loadDocument = async () => {
    if (!document) return;

    setIsLoading(true);
    setError('');

    try {
      // Use fetch directly with auth token to avoid Content-Type issues with blob responses
      const token = authService.getApiToken();
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/documents/${document.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let server set it based on file type
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        setDocumentBlob(blob);

        // Detect file type from blob
        const contentType = response.headers.get('content-type') || '';
        const fileName = document.original_file_name || '';
        
        // Determine file type
        let fileType = document.file_type || contentType || fileName;
        setDetectedFileType(fileType);
        
        console.log('Document loaded:', {
          fileName,
          contentType,
          fileType: document.file_type,
          detected: fileType,
        });

        // Create object URL for viewing
        const url = URL.createObjectURL(blob);
        setDocumentUrl(url);

        // If it's a text file, read the content
        if (isTextFile(fileType)) {
          const text = await blob.text();
          setTextContent(text);
        }
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Failed to load document:', response.status, errorText);
        setError(`Failed to load document: ${response.status} ${response.statusText}`);
      }
    } catch (err: any) {
      console.error('Error loading document:', err);
      setError(err.message || 'Error loading document. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const isTextFile = (fileType: string): boolean => {
    const textTypes = ['text/plain', 'text/csv', 'text/html', 'text/css', 'text/javascript', 'application/json'];
    return textTypes.some(type => fileType.toLowerCase().includes(type)) ||
           fileType.toLowerCase().endsWith('.txt') ||
           fileType.toLowerCase().endsWith('.csv') ||
           fileType.toLowerCase().endsWith('.json') ||
           fileType.toLowerCase().endsWith('.html') ||
           fileType.toLowerCase().endsWith('.css') ||
           fileType.toLowerCase().endsWith('.js');
  };

  const isImageFile = (fileType: string): boolean => {
    return fileType.toLowerCase().startsWith('image/') ||
           ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].some(ext => 
             fileType.toLowerCase().endsWith(ext)
           );
  };

  const isPdfFile = (fileType: string): boolean => {
    if (!fileType) return false;
    const lower = fileType.toLowerCase();
    return lower === 'application/pdf' ||
           lower.includes('pdf') ||
           lower.endsWith('.pdf');
  };

  const canViewInline = (): boolean => {
    if (!document) return false;
    const fileType = detectedFileType || document.file_type || document.original_file_name || '';
    console.log('Checking if can view inline:', { fileType, detectedFileType, isPdf: isPdfFile(fileType), isImage: isImageFile(fileType), isText: isTextFile(fileType) });
    return isPdfFile(fileType) || isImageFile(fileType) || isTextFile(fileType);
  };

  const handleDownload = () => {
    if (documentUrl && document) {
      const a = window.document.createElement('a');
      a.href = documentUrl;
      a.download = document.original_file_name;
      a.click();
    }
  };

  if (!isOpen || !document) return null;

  const fileType = detectedFileType || document.file_type || document.original_file_name || '';
  const canView = canViewInline();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-lg font-bold truncate" style={{ color: '#1F2937' }}>
              {document.original_file_name}
            </h2>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
              {document.company_name || `Company #${document.company_id}`} • {formatFileSize(document.file_size)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canView && documentUrl && (
              <button
                onClick={handleDownload}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-50"
                style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
              >
                <svg className="w-4 h-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent mb-3"
                  style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                />
                <p className="text-sm" style={{ color: '#6B7280' }}>Loading document...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-lg border" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#DC2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            </div>
          )}

          {!isLoading && !error && documentUrl && (
            <>
              {isPdfFile(detectedFileType || document.file_type || document.original_file_name || '') && (
                <div className="w-full flex flex-col items-center">
                  {/* Page Navigation - Only render once */}
                  {numPages !== null && (
                    <div key="page-navigation" className="mb-4 flex items-center gap-3">
                      <button
                        onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                        disabled={pageNumber <= 1}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                      >
                        <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>
                      <span className="text-sm font-medium px-3 py-1.5 rounded-lg" style={{ color: '#6B7280', backgroundColor: '#F9FAFB' }}>
                        Page {pageNumber} of {numPages}
                      </span>
                      <button
                        onClick={() => setPageNumber(prev => Math.min(numPages || 1, prev + 1))}
                        disabled={pageNumber >= (numPages || 1)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                      >
                        Next
                        <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  {/* PDF Viewer */}
                  <div className="border rounded-lg overflow-auto bg-gray-50 flex justify-center" style={{ borderColor: '#E5E7EB', maxHeight: 'calc(90vh - 280px)' }}>
                    <Document
                      file={documentUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(err) => {
                        console.error('PDF load error:', err);
                        setError('Failed to load PDF. Please try downloading it instead.');
                      }}
                      loading={
                        <div className="flex items-center justify-center p-12">
                          <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent mb-3"
                              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                          />
                            <p className="text-sm" style={{ color: '#6B7280' }}>Loading PDF...</p>
                          </div>
                        </div>
                      }
                    >
                      <Page
                        key={`pdf-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={Math.min(700, Math.max(500, window.innerWidth - 200))}
                      />
                    </Document>
                  </div>
                </div>
              )}

              {isImageFile(detectedFileType || document.file_type || document.original_file_name || '') && (
                <div className="flex items-center justify-center min-h-[400px]">
                  <img
                    src={documentUrl}
                    alt={document.original_file_name}
                    className="max-w-full max-h-[calc(90vh - 200px)] object-contain rounded-lg shadow-sm"
                  />
                </div>
              )}

              {isTextFile(detectedFileType || document.file_type || document.original_file_name || '') && textContent && (
                <div className="border rounded-lg p-4 bg-gray-50 overflow-auto" style={{ borderColor: '#E5E7EB', maxHeight: 'calc(90vh - 200px)' }}>
                  <pre className="text-sm whitespace-pre-wrap break-words font-mono" style={{ color: '#1F2937' }}>
                    {textContent}
                  </pre>
                </div>
              )}

              {!canView && (
                <div className="flex flex-col items-center justify-center min-h-[400px] text-center py-12">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#F3F4F6' }}>
                    <svg className="w-8 h-8" style={{ color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  </div>
                  <p className="text-base font-semibold mb-2" style={{ color: '#1F2937' }}>
                    Preview not available
                  </p>
                  <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                    {fileType || 'Unknown file type'}
                  </p>
                  {documentUrl && (
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md"
                      style={{ backgroundColor: COLORS.admin.primary }}
                    >
                      <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download to View
                    </button>
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

